import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { BillingAddressInput, CustomerAddress } from 'lib/types'
import { Form, Grid, Toast } from 'lib/ui'
import { onlyNumbersInterceptor } from 'lib/shared'
import { useCustomer, useDirectoryOptions } from 'lib/core/hooks'
import { DEFAULT_COUNTRY_CODE } from 'lib/core/resources/constants'
import { useCheckout, useCheckoutContext, AddressPicker, BillingAddressFormActions } from 'lib/modules/checkout'
import {
  billingAddressFormValidation as validation,
  billingAddressFormSchema as schema,
  BillingAddressFormValues as Values
} from './schema'

interface Props {
  onSubmit?: () => void
  onCancel?: () => void
}

export const BillingAddressForm = ({ onSubmit, onCancel }: Props) => {
  const intl = useIntl()
  const { setState } = useCheckoutContext()
  const { setBillingAddress, checkoutSchema } = useCheckout()
  const { addresses } = useCustomer()
  const address = checkoutSchema?.billingAddress
  const [addressId, setAddressId] = useState<number | undefined>()
  const [countryCode, setCountryCode] = useState<string>(address?.countryCode ? address.countryCode : DEFAULT_COUNTRY_CODE)

  const {
    countryInfoOptions,
    countryInfoIsFetching,
    postalCodeIsOptional,
    regionOptions,
    regionIsRequired
  } = useDirectoryOptions({ countryCode })

  const handleCountryChange = ({ value, name, form }: any) => { // TODO: Type
    const { setFieldValue, setFieldTouched } = form
    setCountryCode(value)
    setFieldValue('regionCode', '', false)
    setFieldTouched('regionCode', false, false)
  }

  const handleBillingSelect = (values?: CustomerAddress) => {
    if (values) {
      const {
        id,
        ...address
      } = values

      setAddressId(id)
      handleBillingAddress(address)
    } else {
      setAddressId(undefined)
    }
  }

  const handleBillingAddress = (values: Values) => {
    const {
      firstName,
      lastName,
      company,
      street,
      street2,
      city,
      regionCode,
      postalCode,
      countryCode,
      phoneNumber
    } = values

    const address: BillingAddressInput = {
      city,
      countryCode,
      firstName,
      lastName,
      company,
      postalCode,
      regionCode,
      street,
      street2,
      phoneNumber
    }

    setBillingAddress({ address }, {
      callback: {
        onSuccess: () => {
          setState((state) => ({
            ...state,
            billingAddressErrors: {}
          }))
        },
        onFailure: () => {
          Toast.toastFailure({ messageId: 'checkout.billingAddress.failure' })
        }
      }
    })
    onSubmit?.()
  }

  return (
    <div>
      <Formik
        onSubmit={handleBillingAddress}
        validationSchema={validation({ intl, countryCode, postalCodeIsOptional, regionIsRequired })}
        validateOnChange
        initialValues={{
          ...schema,
          ...address,
          countryCode
        }}
      >
        <Form.Wrapper>
          <Form.FormikObserver<Values>
            onValid={() => {
              setState((state) => ({
                ...state,
                billingAddressErrors: undefined
              }))
            }}
            onError={(errors) => {
              setState((state) => ({
                ...state,
                billingAddressErrors: errors
              }))
            }}
          />

          <AddressPicker
            value={addressId}
            addresses={addresses}
            onChange={handleBillingSelect}
            useAnotherLabel='Use another billing address'
          />

          {!addressId && (
            <>
              <Grid>
                <div>
                  <Field
                    name='firstName'
                    label={intl.formatMessage({
                      id: 'checkout.billing.label.firstName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='lastName'
                    label={intl.formatMessage({
                      id: 'checkout.billing.label.lastName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
              </Grid>

              <Grid>
                <Field
                  name='company'
                  label={intl.formatMessage({
                    id: 'checkout.shipping.label.company'
                  })}
                  component={Form.Input}
                  hasAutoPlaceholder
                />
                <Field
                  name='phoneNumber'
                  label={intl.formatMessage({
                    id: 'checkout.billing.label.phoneNumber'
                  })}
                  component={Form.Input}
                  interceptChange={onlyNumbersInterceptor}
                  hasAutoPlaceholder
                  isRequired
                />
              </Grid>

              <Field
                name='street'
                label={intl.formatMessage({
                  id: 'checkout.billing.label.street'
                })}
                component={Form.Input}
                hasAutoPlaceholder
                isRequired
              />

              <Field
                name='street2'
                label={intl.formatMessage({
                  id: 'checkout.billing.label.street2'
                })}
                component={Form.Input}
                hasAutoPlaceholder
              />

              <Grid>
                <div>
                  <Field
                    name='city'
                    label={intl.formatMessage({
                      id: 'checkout.billing.label.city'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='regionCode'
                    label={intl.formatMessage({
                      id: 'checkout.billing.label.region'
                    })}
                    component={Form.Select}
                    options={regionOptions}
                    isLoading={countryInfoIsFetching}
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='postalCode'
                    label={intl.formatMessage({
                      id: 'checkout.billing.label.postalCode'
                    })}
                    component={Form.Input}
                    interceptChange={onlyNumbersInterceptor}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
              </Grid>

              <Field
                name='countryCode'
                label={intl.formatMessage({
                  id: 'checkout.billing.label.country'
                })}
                component={Form.Select}
                options={countryInfoOptions}
                interceptChange={handleCountryChange}
                isLoading={countryInfoIsFetching}
                isRequired
              />

              <BillingAddressFormActions onCancel={onCancel} />
            </>
          )}
        </Form.Wrapper>
      </Formik>
    </div>
  )
}
